import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, FormControl, IconControl, InfoControl, SearchInput } from "spark-core-dx/components";
import "./cardGridControl.scss";

const defaultRowKey = (row, index) => row?._id ?? row?.id ?? row?.UniqueId ?? row?.uniqueId ?? row?.SourceObjectTargetId ?? row?.sourceObjectTargetId ?? index;

const asArray = (value) => Array.isArray(value) ? value : [];

const normalizeMetadata = (metadata) => {
  if (!metadata) return null;
  if (Array.isArray(metadata)) return metadata[0] ?? null;
  if (typeof metadata.first === "function") return metadata.first();
  return metadata;
};

const getFieldName = (field) => field?.FieldName ?? field?.fieldName;
const normalizeFieldKey = (value) => String(value || "").trim().toLowerCase().replace(/[\s_-]/g, "");
const CLIENT_FIELD_KEYS = new Set(["client", "clientid", "clientname", "clientdisplayname"]);
const isClientField = (field) => {
  const fieldName = normalizeFieldKey(getFieldName(field));
  const displayName = normalizeFieldKey(field?.DisplayName ?? field?.displayName);
  return CLIENT_FIELD_KEYS.has(fieldName) || displayName === "client" || displayName === "clientid";
};
const isVisibleField = (field) => !isClientField(field)
  && field?.IsDeleted !== true
  && field?.isDeleted !== true
  && field?.IsActive !== false
  && field?.isActive !== false;
export const isCardGridVisibleField = isVisibleField;

const getRowFieldName = (row, fieldName) => {
  if (!row || !fieldName) return fieldName;
  if (Object.prototype.hasOwnProperty.call(row, fieldName)) return fieldName;
  return Object.keys(row).find((key) => String(key).toLowerCase() === String(fieldName).toLowerCase()) ?? fieldName;
};

const getCaseInsensitiveValue = (source, fieldName) => {
  if (!source || !fieldName) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, fieldName)) return source[fieldName];
  const matchingKey = Object.keys(source).find((key) => String(key).toLowerCase() === String(fieldName).toLowerCase());
  return matchingKey ? source[matchingKey] : undefined;
};

const getFieldSequence = (field) => {
  const gridSequence = Number(field?.GridSequence ?? field?.gridSequence);
  if (Number.isFinite(gridSequence) && gridSequence > 0) return gridSequence;
  const displaySequence = Number(field?.DisplaySequence ?? field?.displaySequence);
  return Number.isFinite(displaySequence) ? displaySequence : 9999;
};

const getFieldLabel = (field) => field?.DisplayName ?? field?.displayName ?? getFieldName(field);
const getCardFieldLabel = (field) => field?.CardDisplayName ?? field?.cardDisplayName ?? field?.CardLabel ?? field?.cardLabel ?? getFieldLabel(field);

const getRowValue = (row, field) => {
  const fieldName = getFieldName(field);
  if (!fieldName) return undefined;
  const rowFieldName = getRowFieldName(row, fieldName);
  return row?.[rowFieldName];
};

const isInactiveValue = (value) => value === false || String(value).toLowerCase() === "false" || value === 0 || String(value) === "0";
const ACTIVE_FILTER_MODES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  BOTH: "both"
};
const ACTIVE_FILTER_OPTIONS = [
  { key: ACTIVE_FILTER_MODES.ACTIVE, label: "Active" },
  { key: ACTIVE_FILTER_MODES.INACTIVE, label: "Inactive" },
  { key: ACTIVE_FILTER_MODES.BOTH, label: "Both" }
];
const TYPE_FILTER_ALL = "all";

const normalizeActiveFilterOptions = (options) => {
  const configuredOptions = asArray(options).length ? asArray(options) : ACTIVE_FILTER_OPTIONS;
  return configuredOptions
    .map((option) => {
      const key = String(option?.key ?? option?.Key ?? "").trim().toLowerCase();
      if (!key) return null;
      return {
        key,
        label: option?.label ?? option?.Label ?? option?.title ?? option?.Title ?? key
      };
    })
    .filter(Boolean);
};

const getDefaultActiveFilterOptionKey = (options) => {
  const normalizedOptions = normalizeActiveFilterOptions(options);
  return normalizedOptions.find((option) => option.key === ACTIVE_FILTER_MODES.ACTIVE)?.key
    ?? normalizedOptions[0]?.key
    ?? ACTIVE_FILTER_MODES.ACTIVE;
};

const normalizeActiveFilterMode = (value, activeFilterOptions = ACTIVE_FILTER_OPTIONS) => {
  const normalizedOptions = normalizeActiveFilterOptions(activeFilterOptions);
  const optionKeys = normalizedOptions.map((option) => option.key);
  const defaultKey = getDefaultActiveFilterOptionKey(normalizedOptions);
  if (value === true) return optionKeys.includes(ACTIVE_FILTER_MODES.ACTIVE) ? ACTIVE_FILTER_MODES.ACTIVE : defaultKey;
  if (value === false) return optionKeys.includes(ACTIVE_FILTER_MODES.BOTH) ? ACTIVE_FILTER_MODES.BOTH : defaultKey;
  const normalized = String(value || "").trim().toLowerCase();
  return optionKeys.includes(normalized) ? normalized : defaultKey;
};

const isRowActiveForFilter = (row, activeFilterFieldName) => {
  if (!activeFilterFieldName) return true;
  const rowFieldName = getRowFieldName(row, activeFilterFieldName);
  if (!row || !Object.prototype.hasOwnProperty.call(row, rowFieldName)) return true;
  return !isInactiveValue(row[rowFieldName]);
};

const getDefaultActiveFilterMode = (rows, activeFilterFieldName, activeFilterDefaultMode, activeFilterDefaultOn, activeFilterOptions = ACTIVE_FILTER_OPTIONS) => {
  if (activeFilterDefaultMode !== undefined && activeFilterDefaultMode !== null && activeFilterDefaultMode !== "") {
    return normalizeActiveFilterMode(activeFilterDefaultMode, activeFilterOptions);
  }

  const safeRows = asArray(rows);
  if (safeRows.length && !safeRows.some((row) => isRowActiveForFilter(row, activeFilterFieldName))) {
    return normalizeActiveFilterMode(ACTIVE_FILTER_MODES.BOTH, activeFilterOptions);
  }

  return normalizeActiveFilterMode(activeFilterDefaultOn, activeFilterOptions);
};

export const filterCardGridRowsByActive = (rows = [], activeFilterFieldName = "IsActive", activeFilterMode = ACTIVE_FILTER_MODES.ACTIVE) => {
  const safeRows = asArray(rows);
  const mode = normalizeActiveFilterMode(activeFilterMode);
  if (mode === ACTIVE_FILTER_MODES.BOTH) return safeRows;
  return safeRows.filter((row) => {
    const isActive = isRowActiveForFilter(row, activeFilterFieldName);
    return mode === ACTIVE_FILTER_MODES.INACTIVE ? !isActive : isActive;
  });
};

const normalizeTypeFilterMode = (value, typeFilterOptions = []) => {
  const normalized = String(value || "").trim().toLowerCase();
  const optionKeys = asArray(typeFilterOptions).map((option) => String(option?.key ?? option?.Key ?? "").trim().toLowerCase());
  return optionKeys.includes(normalized) ? normalized : TYPE_FILTER_ALL;
};

const getTypeFilterOptionValues = (option) => {
  const rawValues = option?.values ?? option?.Values ?? option?.value ?? option?.Value;
  const values = Array.isArray(rawValues) ? rawValues : rawValues === undefined || rawValues === null ? [] : [rawValues];
  return values.map((value) => String(value));
};

export const filterCardGridRowsByType = (rows = [], typeFilterFieldName, typeFilterMode = TYPE_FILTER_ALL, typeFilterOptions = []) => {
  const safeRows = asArray(rows);
  const mode = normalizeTypeFilterMode(typeFilterMode, typeFilterOptions);
  if (!typeFilterFieldName || mode === TYPE_FILTER_ALL) return safeRows;

  const matchingOption = asArray(typeFilterOptions)
    .find((option) => String(option?.key ?? option?.Key ?? "").trim().toLowerCase() === mode);
  const acceptedValues = getTypeFilterOptionValues(matchingOption);
  if (!acceptedValues.length) return safeRows;

  return safeRows.filter((row) => {
    const fieldName = getRowFieldName(row, typeFilterFieldName);
    return acceptedValues.includes(String(row?.[fieldName]));
  });
};

const parseOptions = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getFieldOptions = (field) => parseOptions(field?.DataJson ?? field?.dataJson ?? field?.Options ?? field?.options);

const getCardDescription = (row) => {
  const description = getCaseInsensitiveValue(row, "Description");
  return String(description ?? "").trim();
};

const CardGridInfoButton = ({ description, title }) => {
  const [openInfoMenu, setOpenInfoMenu] = useState(false);
  const infoIconRef = useRef(null);

  if (!description) {
    return null;
  }

  return (
    <span
      className="card-grid-control__info-wrapper"
      onMouseOver={() => setOpenInfoMenu(true)}
      onMouseLeave={() => setOpenInfoMenu(false)}
    >
      <button
        type="button"
        ref={infoIconRef}
        className="card-grid-control__icon-button card-grid-control__info-button"
        aria-label={`Information for ${title}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpenInfoMenu((previous) => !previous);
        }}
        onFocus={() => setOpenInfoMenu(true)}
        onBlur={() => setOpenInfoMenu(false)}
      >
        <IconControl
          className="card-grid-control__info-icon"
          iconKey="info"
          width="1.125rem"
          height="1.125rem"
        />
      </button>
      <InfoControl
        openInfoMenu={openInfoMenu}
        setOpenInfoMenu={setOpenInfoMenu}
        iconRef={infoIconRef}
        offsetTop={8}
        location="bottom center"
        maxWidth={24}
      >
        <div className="card-grid-control__info-menu-content">{description}</div>
      </InfoControl>
    </span>
  );
};

const getResolverForField = (field, valueDisplayResolvers) => {
  if (!valueDisplayResolvers) return null;
  if (typeof valueDisplayResolvers === "function") return valueDisplayResolvers;
  const fieldName = getFieldName(field);
  const normalizedFieldName = String(fieldName || "").toLowerCase();
  if (valueDisplayResolvers instanceof Map) {
    return valueDisplayResolvers.get(fieldName) ?? valueDisplayResolvers.get(normalizedFieldName) ?? null;
  }
  return valueDisplayResolvers[fieldName] ?? valueDisplayResolvers[normalizedFieldName] ?? null;
};

const getDefaultResolver = (valueDisplayResolvers) => {
  if (!valueDisplayResolvers || typeof valueDisplayResolvers === "function") return null;
  if (valueDisplayResolvers instanceof Map) {
    return valueDisplayResolvers.get("__default") ?? valueDisplayResolvers.get("*") ?? null;
  }
  return valueDisplayResolvers.__default ?? valueDisplayResolvers["*"] ?? null;
};

const resolveLookupValue = (lookup, value) => {
  if (!lookup || value === undefined || value === null || value === "") return "";
  const key = String(value);
  const normalizedKey = key.toLowerCase();
  if (lookup instanceof Map) {
    return lookup.get(value) ?? lookup.get(key) ?? lookup.get(normalizedKey) ?? "";
  }
  if (typeof lookup === "object") {
    return lookup[value] ?? lookup[key] ?? lookup[normalizedKey] ?? "";
  }
  return "";
};

const resolveDisplayValue = (value, row, field, valueDisplayResolvers) => {
  const resolver = getResolverForField(field, valueDisplayResolvers);
  if (typeof resolver === "function") {
    const resolved = resolver(value, row, field);
    if (resolved !== undefined && resolved !== null && resolved !== "") return String(resolved);
  } else if (resolver) {
    const resolved = resolveLookupValue(resolver, value);
    if (resolved !== undefined && resolved !== null && resolved !== "") return String(resolved);
  }

  const defaultResolver = getDefaultResolver(valueDisplayResolvers);
  if (typeof defaultResolver === "function") {
    const resolved = defaultResolver(value, row, field);
    return resolved === undefined || resolved === null ? "" : String(resolved);
  }
  const resolved = resolveLookupValue(defaultResolver, value);
  return resolved === undefined || resolved === null ? "" : String(resolved);
};

export const formatCardGridValue = (value, field, row, valueDisplayResolvers) => {
  if (value === undefined || value === null || value === "") return "Not set";
  const resolvedDisplayValue = resolveDisplayValue(value, row, field, valueDisplayResolvers);
  if (resolvedDisplayValue) return resolvedDisplayValue;
  const matchingOption = getFieldOptions(field).find((option) => String(option?.Value ?? option?.value) === String(value));
  if (matchingOption) return String(matchingOption.Text ?? matchingOption.text ?? matchingOption.DisplayName ?? matchingOption.displayName ?? value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

export const buildCardGridSearchText = (row, fields, valueDisplayResolvers) => fields
  .map((field) => formatCardGridValue(getRowValue(row, field), field, row, valueDisplayResolvers))
  .join(" ")
  .toLowerCase();

const buildDefaultCardFields = (metadata, fields) => {
  const sourceFields = (asArray(fields).length ? fields : asArray(metadata?.Fields ?? metadata?.fields)).filter(isVisibleField);
  const showOnCard = sourceFields.filter((field) => field?.ShowOnCard === true || field?.showOnCard === true);
  const showOnGrid = sourceFields.filter((field) => field?.ShowOnGrid === true || field?.showOnGrid === true);
  const selected = showOnCard.length ? showOnCard : showOnGrid;
  return selected
    .slice()
    .sort((a, b) => getFieldSequence(a) - getFieldSequence(b))
    .slice(0, 4);
};

const CardGridControl = memo(function CardGridControl({
  className = "",
  rows,
  metadata,
  formMetadata,
  formFields,
  cardFields,
  rowKey = defaultRowKey,
  selectedRowKey,
  onSelectedRowKeyChange,
  selectedRowKeys,
  onSelectedRowKeysChange,
  onEditingChange,
  onCancelEdit,
  onRowChange,
  onRowClick,
  actions,
  cardActionsAlign = "start",
  cardTitleResolver,
  headerIconActions,
  headerIconActionsAfterEdit,
  headerStatusResolver,
  selectedHeaderIconActions,
  toolbarLeadingContent,
  toolbarActions,
  cardClassNameResolver,
  cardFieldVisibleResolver,
  selectableRowDisabledResolver,
  showSelectionLockIcon = true,
  searchable = true,
  readOnly = false,
  disabled = false,
  disableInlineEdit = false,
  disableCardClickEdit = false,
  emptyContent = "No records are available.",
  toggleFieldName,
  toggleEnabled = false,
  showToggleWhenSelected = false,
  toggleActiveLabel = "Deactivate",
  toggleInactiveLabel = "Activate",
  onToggleChange,
  activeFilterEnabled = true,
  activeFilterFieldName = "IsActive",
  activeFilterDefaultOn = true,
  activeFilterDefaultMode,
  activeFilterOptions,
  activeFilterAriaLabel = "Active status filter",
  activeFilterRowPredicate,
  typeFilterEnabled = false,
  typeFilterFieldName,
  typeFilterOptions,
  typeFilterDefaultMode = TYPE_FILTER_ALL,
  typeFilterAriaLabel = "Source target type filter",
  formGroupId,
  valueDisplayResolvers,
  selectable = false,
  multiSelect = false,
  minCardWidth = "14rem",
  maxCardWidth = "1fr",
  maxColumns
}) {
  const normalizedMetadata = normalizeMetadata(metadata);
  const normalizedFormMetadata = normalizeMetadata(formMetadata) ?? normalizedMetadata;
  const safeRows = asArray(rows);
  const searchShellRef = useRef(null);
  const activeFilterTouchedRef = useRef(false);
  const normalizedActiveFilterOptions = useMemo(
    () => normalizeActiveFilterOptions(activeFilterOptions),
    [activeFilterOptions]
  );
  const [searchText, setSearchText] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [activeFilterMode, setActiveFilterMode] = useState(() => getDefaultActiveFilterMode(rows, activeFilterFieldName, activeFilterDefaultMode, activeFilterDefaultOn, normalizedActiveFilterOptions));
  const [typeFilterMode, setTypeFilterMode] = useState(() => normalizeTypeFilterMode(typeFilterDefaultMode, typeFilterOptions));
  const [editingRowKey, setEditingRowKey] = useState("");
  const [draftRow, setDraftRow] = useState(null);
  const selectedKeySet = useMemo(
    () => new Set(asArray(selectedRowKeys).map((key) => String(key))),
    [selectedRowKeys]
  );
  const effectiveCardFields = useMemo(
    () => buildDefaultCardFields(normalizedMetadata, cardFields),
    [cardFields, normalizedMetadata]
  );
  const allFormFields = useMemo(
    () => asArray(formFields).length ? asArray(formFields) : asArray(normalizedFormMetadata?.Fields ?? normalizedFormMetadata?.fields),
    [formFields, normalizedFormMetadata]
  );
  const visibleFormFields = useMemo(
    () => allFormFields.filter(isVisibleField),
    [allFormFields]
  );
  const normalizedSearch = searchText.trim().toLowerCase();
  const effectiveSearchable = searchable && safeRows.length > 1;
  const safeRowCount = safeRows.length;
  const normalizedTypeFilterOptions = useMemo(() => {
    const configuredOptions = asArray(typeFilterOptions);
    const hasAllOption = configuredOptions.some((option) =>
      String(option?.key ?? option?.Key ?? "").trim().toLowerCase() === TYPE_FILTER_ALL);
    return hasAllOption
      ? configuredOptions
      : [{ key: TYPE_FILTER_ALL, label: "All", title: "All target types" }, ...configuredOptions];
  }, [typeFilterOptions]);
  const filteredRows = useMemo(() => {
    const activeFilteredRows = activeFilterEnabled
      ? typeof activeFilterRowPredicate === "function"
        ? safeRows.filter((row, index) => {
          const key = rowKey(row, index);
          return activeFilterRowPredicate(row, {
            mode: activeFilterMode,
            isActive: isRowActiveForFilter(row, activeFilterFieldName),
            isSelected: selectedKeySet.has(String(key)),
            rowKey: key
          }) !== false;
        })
        : filterCardGridRowsByActive(safeRows, activeFilterFieldName, activeFilterMode)
      : safeRows;
    const typeFilteredRows = typeFilterEnabled
      ? filterCardGridRowsByType(activeFilteredRows, typeFilterFieldName, typeFilterMode, normalizedTypeFilterOptions)
      : activeFilteredRows;
    if (!normalizedSearch) return typeFilteredRows;
    const searchableFields = visibleFormFields.length ? visibleFormFields : effectiveCardFields;
    return typeFilteredRows.filter((row) => buildCardGridSearchText(row, searchableFields, valueDisplayResolvers).includes(normalizedSearch));
  }, [activeFilterEnabled, activeFilterFieldName, activeFilterMode, activeFilterRowPredicate, effectiveCardFields, normalizedSearch, normalizedTypeFilterOptions, rowKey, safeRows, selectedKeySet, typeFilterEnabled, typeFilterFieldName, typeFilterMode, valueDisplayResolvers, visibleFormFields]);
  const editingRow = safeRows.find((row, index) => String(rowKey(row, index)) === String(editingRowKey)) ?? null;
  const isEditing = !selectable && Boolean(editingRow);
  const orderedRows = isEditing ? [editingRow] : filteredRows;
  const isReadOnly = readOnly || disabled;
  const toolbarDisabled = disabled || isEditing;
  const normalizedMaxColumns = Number.isFinite(Number(maxColumns)) && Number(maxColumns) > 0
    ? Math.floor(Number(maxColumns))
    : null;
  const renderedEmptyContent = safeRows.length && (normalizedSearch
    || (activeFilterEnabled && activeFilterMode !== ACTIVE_FILTER_MODES.BOTH)
    || (typeFilterEnabled && typeFilterMode !== TYPE_FILTER_ALL))
    ? "No matching records found."
    : emptyContent;
  const styleVars = {
    "--card-grid-min-width": minCardWidth,
    "--card-grid-max-width": maxCardWidth,
    ...(normalizedMaxColumns ? {
      "--card-grid-column-cap-width": `calc((100% - ${(normalizedMaxColumns - 1) * 0.75}rem) / ${normalizedMaxColumns})`
    } : {})
  };
  const toolbarActionList = asArray(toolbarActions);

  useEffect(() => {
    if (selectable) return;
    if (selectedRowKey === undefined) return;
    const normalizedSelectedKey = String(selectedRowKey ?? "");

    if (!normalizedSelectedKey) {
      if (editingRowKey) {
        setEditingRowKey("");
        setDraftRow(null);
        onEditingChange?.(false, "", null);
      }
      return;
    }

    if (String(editingRowKey) === normalizedSelectedKey) return;

    const selectedRow = safeRows.find((row, index) => String(rowKey(row, index)) === normalizedSelectedKey) ?? null;
    if (!selectedRow) return;

    setEditingRowKey(normalizedSelectedKey);
    setDraftRow(selectedRow);
    onEditingChange?.(true, normalizedSelectedKey, selectedRow);
  }, [editingRowKey, onEditingChange, rowKey, safeRows, selectable, selectedRowKey]);

  useEffect(() => {
    if (selectable) return;
    if (!editingRowKey) return;
    if (!editingRow) {
      setEditingRowKey("");
      setDraftRow(null);
      onSelectedRowKeyChange?.("", null);
      onEditingChange?.(false, "", null);
      return;
    }

    setDraftRow((previous) => previous ? previous : editingRow);
  }, [editingRow, editingRowKey, onEditingChange, onSelectedRowKeyChange, selectable]);

  useEffect(() => {
    if (!searchExpanded) return;
    const input = searchShellRef.current?.querySelector?.("input");
    input?.focus?.();
  }, [searchExpanded]);

  useEffect(() => {
    if (!activeFilterEnabled || activeFilterTouchedRef.current) return;
    setActiveFilterMode(getDefaultActiveFilterMode(safeRows, activeFilterFieldName, activeFilterDefaultMode, activeFilterDefaultOn, normalizedActiveFilterOptions));
  // Row edits such as active toggles should not rewrite the user's current filter.
  // Re-evaluate row-derived defaults only when the row set size changes, such as initial load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterDefaultMode, activeFilterDefaultOn, activeFilterEnabled, activeFilterFieldName, normalizedActiveFilterOptions, safeRowCount]);

  useEffect(() => {
    if (!typeFilterEnabled) return;
    setTypeFilterMode((previous) => normalizeTypeFilterMode(previous, normalizedTypeFilterOptions));
  }, [normalizedTypeFilterOptions, typeFilterEnabled]);

  const handleEdit = (row, index) => {
    if (disabled) return;
    const key = rowKey(row, index);
    setEditingRowKey(key);
    setDraftRow(row);
    onSelectedRowKeyChange?.(key, row);
    onEditingChange?.(true, key, row);
    onRowClick?.(row, key);
  };

  const handleSelect = (row, index) => {
    if (disabled || readOnly) return;
    const key = rowKey(row, index);
    if (typeof selectableRowDisabledResolver === "function" && selectableRowDisabledResolver(row, key, { index })) return;
    const stringKey = String(key);
    const nextKeys = multiSelect
      ? selectedKeySet.has(stringKey)
        ? asArray(selectedRowKeys).filter((selectedKey) => String(selectedKey) !== stringKey)
        : [...asArray(selectedRowKeys), key]
      : selectedKeySet.has(stringKey)
        ? []
        : [key];
    onSelectedRowKeysChange?.(nextKeys, row, key);
    onRowClick?.(row, key);
  };

  const handleFormChange = (value, fieldName, updatedItem) => {
    if (!draftRow) return;
    setDraftRow({
      ...draftRow,
      ...(updatedItem || {}),
      [fieldName]: value
    });
  };

  const closeEditor = ({ notifyCancel = false } = {}) => {
    const canceledKey = editingRowKey;
    const canceledRow = editingRow;
    const canceledDraft = draftRow;
    setEditingRowKey("");
    setDraftRow(null);
    onSelectedRowKeyChange?.("", null);
    onEditingChange?.(false, "", null);
    if (notifyCancel) {
      onCancelEdit?.(canceledRow, canceledKey, canceledDraft);
    }
  };

  const handleSaveEdit = () => {
    if (!draftRow) return;
    onRowChange?.(draftRow);
    closeEditor();
  };

  const handleToggle = (row, key) => {
    if (disabled || !toggleFieldName) return;
    const fieldName = getRowFieldName(row, toggleFieldName);
    const nextValue = !Boolean(row?.[fieldName]);
    const updatedRow = {
      ...row,
      [fieldName]: nextValue
    };
    onToggleChange?.(updatedRow, nextValue, row, key);
    if (!onToggleChange) {
      onRowChange?.(updatedRow);
    }
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    if (value) {
      setSearchExpanded(true);
    }
  };

  const renderCard = (row, orderedIndex) => {
    const originalIndex = safeRows.findIndex((candidate) => candidate === row);
    const renderIndex = originalIndex >= 0 ? originalIndex : orderedIndex;
    const key = rowKey(row, renderIndex);
    const reactKey = `${String(key)}::${orderedIndex}`;
    const selected = selectable ? selectedKeySet.has(String(key)) : String(key) === String(editingRowKey);
    const selectionDisabled = selectable && typeof selectableRowDisabledResolver === "function"
      ? selectableRowDisabledResolver(row, key, { index: renderIndex, selected }) === true
      : false;
    const cardReadOnly = isReadOnly || selectionDisabled;
    const cardInteractive = !cardReadOnly && (selectable || !disableCardClickEdit);
    const titleField = effectiveCardFields[0];
    const resolvedCardTitle = typeof cardTitleResolver === "function"
      ? cardTitleResolver(row, { key, index: renderIndex, titleField })
      : "";
    const title = resolvedCardTitle
      || formatCardGridValue(getRowValue(row, titleField), titleField, row, valueDisplayResolvers)
      || row?.DisplayName
      || row?.Name
      || key;
    const cardActions = asArray(actions);
    const cardHeaderIconActions = asArray(headerIconActions);
    const cardHeaderIconActionsAfterEdit = asArray(headerIconActionsAfterEdit);
    const selectedCardHeaderIconActions = asArray(selectedHeaderIconActions);
    const headerStatus = typeof headerStatusResolver === "function"
      ? headerStatusResolver(row, { key, index: renderIndex, selected, selectionDisabled })
      : null;
    const toggleField = toggleFieldName ? getRowFieldName(row, toggleFieldName) : "";
    const toggleActive = Boolean(toggleField ? row?.[toggleField] : false);
    const canRenderToggle = toggleEnabled
      && toggleFieldName
      && !isReadOnly
      && (!selected || showToggleWhenSelected);
    const description = getCardDescription(row);
    const renderHeaderIconAction = (action, actionScope) => {
      const actionKey = action.Key ?? action.key ?? action.DisplayName ?? action.Title ?? action.title ?? action.iconKey ?? action.IconKey;
      const actionTitle = action.Title ?? action.title ?? action.DisplayName ?? action.displayName ?? "Action";
      const visibleSetting = action.Visible ?? action.visible;
      const disabledSetting = action.Disabled ?? action.disabled;
      const visibleAction = typeof visibleSetting === "function" ? visibleSetting(row, key) : visibleSetting !== false;
      const disabledAction = typeof disabledSetting === "function" ? disabledSetting(row, key) : disabledSetting === true;
      const iconKey = action.IconKey ?? action.iconKey ?? "settings";
      const actionIconSize = action.IconSize ?? action.iconSize ?? "1rem";
      const actionToneSetting = action.Tone ?? action.tone;
      const actionClassNameSetting = action.ClassName ?? action.className ?? "";
      const iconClassNameSetting = action.IconClassName ?? action.iconClassName ?? "";
      const actionTone = typeof actionToneSetting === "function"
        ? actionToneSetting(row, key)
        : actionToneSetting;
      const actionClassName = typeof actionClassNameSetting === "function"
        ? actionClassNameSetting(row, key)
        : actionClassNameSetting;
      const iconClassName = typeof iconClassNameSetting === "function"
        ? iconClassNameSetting(row, key)
        : iconClassNameSetting;
      const normalizedActionTone = String(actionTone ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "-");
      const actionIconClassName = [
        "card-grid-control__action-icon",
        `card-grid-control__action-icon--${String(iconKey).toLowerCase().replace(/[^a-z0-9_-]/g, "-")}`,
        iconClassName
      ].filter(Boolean).join(" ");
      const buttonClassName = [
        "card-grid-control__icon-button",
        normalizedActionTone ? `card-grid-control__icon-button--${normalizedActionTone}` : "",
        actionClassName
      ].filter(Boolean).join(" ");
      if (!visibleAction) return null;
      return (
        <button
          type="button"
          className={buttonClassName}
          key={`${actionScope}-${actionKey}`}
          title={actionTitle}
          aria-label={`${actionTitle} ${title}`}
          disabled={disabledAction}
          onClick={(event) => {
            event.stopPropagation();
            action.ActionCallback?.(row, key);
            action.onClick?.(row, key);
          }}
        >
          <IconControl
            className={actionIconClassName}
            iconKey={iconKey}
            width={actionIconSize}
            height={actionIconSize}
          />
        </button>
      );
    };

    return (
      <div
        role={cardInteractive ? "button" : "group"}
        tabIndex={cardInteractive ? 0 : -1}
        key={reactKey}
        className={[
          "card-grid-control__card",
          selected ? "selected" : "",
          selectionDisabled ? "card-grid-control__card--selection-locked" : "",
          typeof cardClassNameResolver === "function" ? cardClassNameResolver(row, { key, index: renderIndex, selected, selectionDisabled }) : ""
        ].filter(Boolean).join(" ")}
        onClick={() => {
          if (!cardInteractive) return;
          if (selectable) handleSelect(row, renderIndex);
          else handleEdit(row, renderIndex);
        }}
        onKeyDown={(event) => {
          if (cardInteractive && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            if (selectable) handleSelect(row, renderIndex);
            else handleEdit(row, renderIndex);
          }
        }}
        aria-disabled={cardReadOnly}
        aria-pressed={selectable ? selected : undefined}
      >
        {selectionDisabled && showSelectionLockIcon ? (
          <span className="card-grid-control__selection-lock" title="Already selected">
            <span className="material-symbols-rounded card-grid-control__selection-lock-icon" aria-hidden="true">
              check
            </span>
          </span>
        ) : null}
        <div className="card-grid-control__card-header">
          <div className="card-grid-control__card-header-title">
            <strong title={title}>{title}</strong>
            <CardGridInfoButton description={description} title={title} />
          </div>
          <div className="card-grid-control__card-header-actions">
            {headerStatus ? (
              <span
                className={[
                  "card-grid-control__header-status",
                  `card-grid-control__header-status--${String(headerStatus.tone || headerStatus.status || "default").toLowerCase().replace(/[^a-z0-9_-]/g, "-")}`,
                  headerStatus.className || ""
                ].filter(Boolean).join(" ")}
                title={headerStatus.title || headerStatus.label || ""}
                aria-label={headerStatus.title || headerStatus.label || undefined}
              />
            ) : null}
            {canRenderToggle ? (
              <button
                type="button"
                className={`card-grid-control__icon-button card-grid-control__toggle-button${toggleActive ? " active" : ""}`}
                title={toggleActive ? toggleActiveLabel : toggleInactiveLabel}
                aria-label={`${toggleActive ? toggleActiveLabel : toggleInactiveLabel} ${title}`}
                aria-pressed={toggleActive}
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggle(row, key);
                }}
              >
                <span className="material-symbols-rounded card-grid-control__toggle-icon" aria-hidden="true">
                  {toggleActive ? "toggle_on" : "toggle_off"}
                </span>
              </button>
            ) : null}
            {!selected && cardHeaderIconActions.map((action) => renderHeaderIconAction(action, "header"))}
            {selected && selectedCardHeaderIconActions.map((action) => renderHeaderIconAction(action, "selected-header"))}
            {!selectable && !isReadOnly && !disableInlineEdit && !selected ? (
              <button
                type="button"
                className="card-grid-control__icon-button"
                title="Edit"
                aria-label={`Edit ${title}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleEdit(row, originalIndex >= 0 ? originalIndex : orderedIndex);
                }}
              >
                <IconControl
                  className="card-grid-control__edit-icon"
                  iconKey="edit"
                  width="1.5rem"
                  height="1.5rem"
                />
              </button>
            ) : null}
            {!selected && cardHeaderIconActionsAfterEdit.map((action) => renderHeaderIconAction(action, "header-after-edit"))}
            {!selectable && !isReadOnly && selected ? (
              <button
                type="button"
                className="card-grid-control__icon-button"
                title="Cancel edit"
                aria-label={`Cancel edit ${title}`}
                onClick={(event) => {
                  event.stopPropagation();
                  closeEditor({ notifyCancel: true });
                }}
              >
                <IconControl
                  className="card-grid-control__cancel-icon"
                  iconKey="close"
                  width="1.375rem"
                  height="1.375rem"
                />
              </button>
            ) : null}
          </div>
        </div>
        <div className="card-grid-control__card-body">
          {effectiveCardFields.slice(1).filter((field) => (
            typeof cardFieldVisibleResolver === "function"
              ? cardFieldVisibleResolver(field, row, { key, index: renderIndex }) !== false
              : true
          )).map((field) => {
            const fieldValue = formatCardGridValue(getRowValue(row, field), field, row, valueDisplayResolvers);
            const cardFieldLabel = getCardFieldLabel(field);
            const hasCardFieldLabel = String(cardFieldLabel || "").trim().length > 0;
            return (
              <div
                className={`card-grid-control__field${hasCardFieldLabel ? "" : " card-grid-control__field--unlabeled"}`}
                key={getFieldName(field)}
              >
                {hasCardFieldLabel ? <span title={cardFieldLabel}>{cardFieldLabel}</span> : null}
                <strong title={fieldValue}>{fieldValue}</strong>
              </div>
            );
          })}
        </div>
        {cardActions.length ? (
          <div className={[
            "card-grid-control__card-actions",
            cardActionsAlign === "end" ? "card-grid-control__card-actions--end" : ""
          ].filter(Boolean).join(" ")}>
            {cardActions.map((action) => (
              <ButtonControl
                type="standard-small"
                className="card-grid-control__card-action"
                key={action.Key ?? action.DisplayName ?? action.Title}
                onClick={(event) => {
                  event.stopPropagation();
                  action.ActionCallback?.(row, key);
                }}
                disabled={action.Disabled === true}
              >
                {action.DisplayName ?? action.Title ?? "Action"}
              </ButtonControl>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={["card-grid-control", className].filter(Boolean).join(" ")} style={styleVars}>
      {toolbarLeadingContent || effectiveSearchable || activeFilterEnabled || typeFilterEnabled || toolbarActionList.length ? (
        <div className={`card-grid-control__toolbar${isEditing ? " card-grid-control__toolbar--disabled" : ""}`}>
          {toolbarLeadingContent ? (
            <div className="card-grid-control__toolbar-leading">
              {toolbarLeadingContent}
            </div>
          ) : null}
          {effectiveSearchable ? (
            <div
              className={`card-grid-control__search-shell${searchExpanded || searchText ? " expanded" : ""}`}
              ref={searchShellRef}
            >
              <SearchInput
                className="card-grid-control__search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search..."
                disabled={toolbarDisabled || (!searchExpanded && !searchText)}
              />
              <button
                type="button"
                className="card-grid-control__search-toggle"
                title={searchExpanded || searchText ? "Collapse search" : "Search"}
                aria-label={searchExpanded || searchText ? "Collapse search" : "Search"}
                aria-expanded={searchExpanded || Boolean(searchText)}
                disabled={toolbarDisabled}
                onClick={() => {
                  if (toolbarDisabled) return;
                  if (searchExpanded || searchText) {
                    setSearchText("");
                    setSearchExpanded(false);
                    return;
                  }
                  setSearchExpanded(true);
                }}
              >
                <IconControl
                  className="card-grid-control__search-icon"
                  iconKey={searchExpanded || searchText ? "close" : "search"}
                  width="1.375rem"
                  height="1.375rem"
                />
              </button>
            </div>
          ) : null}
          {activeFilterEnabled ? (
            <div className="card-grid-control__active-filter" role="group" aria-label={activeFilterAriaLabel}>
              {normalizedActiveFilterOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={activeFilterMode === option.key ? "active" : ""}
                  aria-pressed={activeFilterMode === option.key}
                  disabled={toolbarDisabled}
                  onClick={() => {
                    if (toolbarDisabled) return;
                    activeFilterTouchedRef.current = true;
                    setActiveFilterMode(option.key);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
          {typeFilterEnabled ? (
            <div className="card-grid-control__type-filter" role="group" aria-label={typeFilterAriaLabel}>
              {normalizedTypeFilterOptions.map((option) => {
                const optionKey = String(option?.key ?? option?.Key ?? "").trim().toLowerCase();
                const label = option?.label ?? option?.Label ?? option?.title ?? option?.Title ?? optionKey;
                const title = option?.title ?? option?.Title ?? label;
                const materialIcon = option?.materialIcon ?? option?.MaterialIcon ?? "";
                const iconKey = option?.iconKey ?? option?.IconKey ?? "";
                return (
                  <button
                    type="button"
                    key={optionKey}
                    className={typeFilterMode === optionKey ? "active" : ""}
                    aria-label={title}
                    aria-pressed={typeFilterMode === optionKey}
                    title={title}
                    disabled={toolbarDisabled}
                    onClick={() => {
                      if (toolbarDisabled) return;
                      setTypeFilterMode(optionKey);
                    }}
                  >
                    {materialIcon ? (
                      <span className="material-symbols-rounded card-grid-control__filter-icon" aria-hidden="true">
                        {materialIcon}
                      </span>
                    ) : iconKey ? (
                      <IconControl
                        className="card-grid-control__filter-icon"
                        iconKey={iconKey}
                        title={title}
                        width="1.125rem"
                        height="1.125rem"
                      />
                    ) : (
                      <span>{label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : null}
          {toolbarActionList.length ? (
            <div className="card-grid-control__toolbar-actions">
              {toolbarActionList.map((action) => {
                const actionKey = action.Key ?? action.key ?? action.DisplayName ?? action.Title ?? action.title;
                const actionTitle = action.Title ?? action.title ?? action.DisplayName ?? action.displayName ?? "Action";
                const actionLabel = action.Label ?? action.label ?? actionTitle;
                const disabledSetting = action.Disabled ?? action.disabled;
                const disabledAction = isEditing || (typeof disabledSetting === "function" ? disabledSetting() : disabledSetting === true);
                const iconKey = action.IconKey ?? action.iconKey ?? "";
                return (
                  <ButtonControl
                    key={actionKey}
                    type={action.Type ?? action.type ?? "standard-small"}
                    className="card-grid-control__toolbar-action"
                    aria-label={actionTitle}
                    title={actionTitle}
                    disabled={isReadOnly || disabledAction}
                    onClick={() => {
                      if (isReadOnly || disabledAction) return;
                      action.ActionCallback?.();
                      action.onClick?.();
                    }}
                  >
                    {iconKey ? <IconControl iconKey={iconKey} title={actionTitle} width="1rem" height="1rem" /> : null}
                    {actionLabel ? <span>{actionLabel}</span> : null}
                  </ButtonControl>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`card-grid-control__body${editingRow ? " card-grid-control__body--editing" : ""}`}>
        {orderedRows.length ? (
          <div className={[
            "card-grid-control__grid",
            normalizedMaxColumns ? "card-grid-control__grid--max-columns" : "",
            editingRow ? "card-grid-control__grid--editing" : ""
          ].filter(Boolean).join(" ")}>
            {orderedRows.map(renderCard)}
          </div>
        ) : (
          <div className="card-grid-control__empty">{renderedEmptyContent}</div>
        )}

        {editingRow && draftRow && normalizedFormMetadata ? (
          <div className="card-grid-control__editor">
            <FormControl
              metadata={normalizedFormMetadata}
              formFields={visibleFormFields}
              itemData={draftRow}
              disableFooter={true}
              disableMinWidth={true}
              disabled={isReadOnly}
              groupId={formGroupId}
              tabLayout="top"
              onChange={handleFormChange}
              onSave={() => {}}
            />
            <div className="card-grid-control__editor-actions">
              <ButtonControl type="cancel" className="card-grid-control__button card-grid-control__button--secondary" onClick={() => closeEditor({ notifyCancel: true })} disabled={isReadOnly}>
                Cancel
              </ButtonControl>
              <ButtonControl type="ok" className="card-grid-control__button card-grid-control__button--primary" onClick={handleSaveEdit} disabled={isReadOnly}>
                Save
              </ButtonControl>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default CardGridControl;
